import { useMemo } from 'react';
import {
  useGetParentPermissionsQuery,
  Permission,
} from '@app-types/blackroom-api';
import { palette } from '@app/shared/ui-kit/colors';
import useGroupDisplayName from '@app/features/Group/hooks/useGroupDisplayName';

export interface GroupPermission {
  groupId: string;
  groupName: string;
  groupColor: string;
  permissions: Permission[];
}

interface UseParentPermissionsResult {
  groupPermissions: GroupPermission[];
  hasPermissions: boolean;
  isLoading: boolean;
  isAtRoot: boolean;
  hasError: boolean;
}

interface UseParentPermissionsOptions {
  /** Skip fetching until this is false (useful for deferring until upload completes) */
  skip?: boolean;
}

/**
 * Hook to fetch and process parent folder permissions
 * Used to display what permissions will be inherited by uploaded files
 */
export function useParentPermissions(
  workspaceId: string,
  parentId?: string,
  options: UseParentPermissionsOptions = {},
): UseParentPermissionsResult {
  const { skip: skipFetch = false } = options;
  const { getDisplayName } = useGroupDisplayName();

  // Fetch parent permissions if parentId exists and not skipped
  const {
    data: parentData,
    loading: isLoading,
    error,
  } = useGetParentPermissionsQuery({
    variables: {
      filter: {
        workspaceId,
        ids: [parentId!],
      },
      limit: 1,
    },
    skip: !parentId || skipFetch,
  });

  const hasError = !!error;

  // Check if at dataroom root (no parent)
  const isAtRoot = !parentId;

  // Extract permissions grouped by group
  const groupPermissions = useMemo((): GroupPermission[] => {
    if (!parentData?.resources?.nodes?.[0]) return [];

    const parentResource = parentData.resources.nodes[0];
    const allAccesses = [
      ...(parentResource.accesses || []),
      ...(parentResource.inheritedAccesses || []),
    ];

    // Group by groupId and merge permissions
    const groupMap = new Map<string, GroupPermission>();

    allAccesses.forEach((access) => {
      if (!access.group) return;

      const existing = groupMap.get(access.groupId);
      if (existing) {
        // Merge permissions without mutating the existing array
        const newPermissions = access.permissions.filter(
          (p) => !existing.permissions.includes(p),
        );
        if (newPermissions.length > 0) {
          groupMap.set(access.groupId, {
            ...existing,
            permissions: [...existing.permissions, ...newPermissions],
          });
        }
      } else {
        groupMap.set(access.groupId, {
          groupId: access.groupId,
          groupName: getDisplayName(access.group),
          groupColor: access.group.color || palette.blackroomBlue,
          permissions: [...access.permissions],
        });
      }
    });

    return Array.from(groupMap.values());
  }, [parentData, getDisplayName]);

  const hasPermissions = groupPermissions.length > 0;

  return {
    groupPermissions,
    hasPermissions,
    isLoading,
    isAtRoot,
    hasError,
  };
}
